import { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Layout from '../components/layout/Layout';
import Button from '../components/common/Button';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { billingService, BillingLineItem, InvoiceStatus, triggerBlobDownload } from '../services/billingService';
import { useInvoice, useUpdateInvoiceEinvoice, useUpdateInvoiceStatus } from '../hooks/useBilling';

const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
  'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function twoDigitsToWords(n: number): string {
  if (n < 20) return ONES[n];
  const ten = Math.floor(n / 10);
  const one = n % 10;
  return one === 0 ? TENS[ten] : `${TENS[ten]} ${ONES[one]}`;
}

function threeDigitsToWords(n: number): string {
  const hundred = Math.floor(n / 100);
  const rest = n % 100;
  if (hundred === 0) return twoDigitsToWords(rest);
  return rest === 0 ? `${ONES[hundred]} Hundred` : `${ONES[hundred]} Hundred ${twoDigitsToWords(rest)}`;
}

// Indian numbering: Crore (1e7) / Lakh (1e5) / Thousand (1e3) / Hundred
function amountInWordsInr(amount: number): string {
  const n = Math.round(amount);
  if (n === 0) return 'INR Zero Only';
  const crore = Math.floor(n / 1_00_00_000);
  const lakh = Math.floor((n % 1_00_00_000) / 1_00_000);
  const thousand = Math.floor((n % 1_00_000) / 1_000);
  const hundred = n % 1_000;
  const parts: string[] = [];
  if (crore > 0) parts.push(`${threeDigitsToWords(crore)} Crore`);
  if (lakh > 0) parts.push(`${threeDigitsToWords(lakh)} Lakh`);
  if (thousand > 0) parts.push(`${threeDigitsToWords(thousand)} Thousand`);
  if (hundred > 0) parts.push(threeDigitsToWords(hundred));
  return `INR ${parts.join(' ')} Only`;
}

const REFERENCE_TYPE_LABELS: Record<string, string> = {
  grn: 'Inbound',
  dispatch: 'Outbound',
};

// Rubber-stamp style status mark shown on the invoice document
const STATUS_STAMP: Record<InvoiceStatus, { classes: string; label: string }> = {
  draft: { classes: 'border-ink-400 text-ink-500', label: 'Draft' },
  sent: { classes: 'border-sky-500 text-sky-600', label: 'Sent' },
  paid: { classes: 'border-green-600 text-green-600', label: 'Paid' },
  cancelled: { classes: 'border-red-500 text-red-600', label: 'Cancelled' },
};

// Matched to the tax-invoice PDF wording (billing-pdf.service.ts) so this
// review page shows the same charge titles the client's PDF will print.
const CHARGE_LABELS: Record<string, string> = {
  storage_pallet: 'WAREHOUSING STORAGE CHARGES',
  storage_sqft: 'WAREHOUSING STORAGE CHARGES',
  storage_fixed: 'WAREHOUSING STORAGE CHARGES',
  cold_storage: 'WAREHOUSING STORAGE CHARGES',
  additional_storage: 'WAREHOUSING STORAGE CHARGES',
  additional_space: 'Extra Space Rent',
  handling: 'Loading /Unloading Charges',
  hamali: 'Loading /Unloading Charges',
  packing: 'Packing Charges',
  label: 'Labelling Charges',
  sticker: 'Hamali Charges',
  other: 'Other Charges',
};

function fmtCurrency(val: string | number) {
  const n = parseFloat(String(val));
  return isNaN(n) ? '—' : `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(d: string) {
  if (!d) return '—';
  const [y, m, day] = d.slice(0, 10).split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${day} ${months[parseInt(m, 10) - 1]} ${y}`;
}

function fmtNum(val: string) {
  const n = parseFloat(val);
  return isNaN(n) ? '—' : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 3 });
}

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canDownload = hasPermission('billing:download');

  const invoiceId = id ? parseInt(id, 10) : null;
  const { data: invoice, isLoading, isError } = useInvoice(invoiceId);
  const statusMutation = useUpdateInvoiceStatus(invoiceId!, invoice?.clientId);
  const einvoiceMutation = useUpdateInvoiceEinvoice(invoiceId!);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [downloading, setDownloading] = useState(false);
  const [einvoiceForm, setEinvoiceForm] = useState({ irnNo: '', ackNo: '', ackDate: '' });
  const [einvoiceErrors, setEinvoiceErrors] = useState<{ irnNo?: string; ackNo?: string }>({});

  useEffect(() => {
    if (invoice) {
      setEinvoiceForm({
        irnNo: invoice.irnNo ?? '',
        ackNo: invoice.ackNo ?? '',
        ackDate: invoice.ackDate ?? '',
      });
    }
  }, [invoice]);

  // Mirrors the backend's own limits (UpdateInvoiceEinvoiceDto) — validated
  // top-to-bottom, stopping at the first invalid field, so only one message
  // shows at a time.
  function validateEinvoiceForm(): boolean {
    if (einvoiceForm.irnNo.length > 100) {
      setEinvoiceErrors({ irnNo: 'IRN Number must not exceed 100 characters.' });
      return false;
    }
    if (einvoiceForm.ackNo.length > 50) {
      setEinvoiceErrors({ ackNo: 'Acknowledgement Number must not exceed 50 characters.' });
      return false;
    }
    setEinvoiceErrors({});
    return true;
  }

  async function saveEinvoice() {
    if (!validateEinvoiceForm()) return;
    try {
      await einvoiceMutation.mutateAsync(einvoiceForm);
      showToast('E-invoice details saved', 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to save e-invoice details';
      showToast(msg, 'error');
    }
  }

  async function transition(status: InvoiceStatus) {
    try {
      await statusMutation.mutateAsync(status);
      showToast(`Invoice marked as ${status}`, 'success');
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to update status';
      showToast(msg, 'error');
    }
  }

  function toggleBreakdown(itemId: number) {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }

  async function downloadPdf() {
    if (!invoice) return;
    setDownloading(true);
    try {
      const blob = await billingService.downloadInvoicePdf(invoice.id);
      triggerBlobDownload(blob, `${invoice.invoiceNumber}.pdf`);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to download invoice';
      showToast(msg, 'error');
    } finally {
      setDownloading(false);
    }
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      </Layout>
    );
  }

  if (isError || !invoice) {
    return (
      <Layout>
        <div className="p-6">
          <p className="text-red-600">Invoice not found.</p>
          <Button variant="outline" onClick={() => navigate('/billing')} className="mt-4">
            Back to Billing
          </Button>
        </div>
      </Layout>
    );
  }

  const stamp = STATUS_STAMP[invoice.status];
  const subtotal = parseFloat(invoice.subtotal);
  const cgstPct = parseFloat(invoice.cgstPercent);
  const cgstAmount = parseFloat(invoice.cgstAmount);
  const sgstPct = parseFloat(invoice.sgstPercent);
  const sgstAmount = parseFloat(invoice.sgstAmount);
  const roundOff = parseFloat(invoice.roundOff);
  const total = parseFloat(invoice.totalAmount);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Top bar: back nav + actions */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-4 lg:mb-6">
          <button
            className="flex items-center gap-2 text-sm text-ink-500 hover:text-ink-800 self-start"
            onClick={() => navigate('/billing')}
          >
            <i className="fas fa-arrow-left" />
            Back to Billing
          </button>

          {/* Status actions - stacked full-width on mobile, inline row on lg+ */}
          <div className="flex flex-col w-full gap-2 lg:flex-row lg:flex-wrap lg:items-center lg:gap-2 lg:w-auto">
              {canDownload && (
                <Button
                  variant="outline"
                  size="sm"
                  icon="fa-download"
                  disabled={downloading}
                  onClick={downloadPdf}
                >
                  {downloading ? 'Downloading…' : 'Download PDF'}
                </Button>
              )}
              {invoice.status === 'draft' && (
                <>
                  <Button
                    variant="info"
                    size="sm"
                    icon="fa-paper-plane"
                    disabled={statusMutation.isPending}
                    onClick={() => transition('sent')}
                  >
                    Mark Sent
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    icon="fa-times"
                    disabled={statusMutation.isPending}
                    onClick={() => transition('cancelled')}
                  >
                    Cancel
                  </Button>
                </>
              )}
              {invoice.status === 'sent' && (
                <>
                  <Button
                    variant="success"
                    size="sm"
                    icon="fa-check"
                    disabled={statusMutation.isPending}
                    onClick={() => transition('paid')}
                  >
                    Mark Paid
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    icon="fa-times"
                    disabled={statusMutation.isPending}
                    onClick={() => transition('cancelled')}
                  >
                    Cancel
                  </Button>
                </>
              )}
          </div>
        </div>

        {/* Invoice document */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-ink-300 max-w-4xl mx-auto mb-4">
          <div className="p-5 sm:p-8 lg:p-10">
            {/* Document header */}
            <div className="flex flex-wrap items-start justify-between gap-4 pb-6 border-b-2 border-ink-900">
              <div>
                <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-ink-900 uppercase">Invoice</h1>
                <p className="font-mono text-sm lg:text-base font-semibold text-ink-700 mt-2 break-all">{invoice.invoiceNumber}</p>
              </div>
              <span className={`inline-block border-2 rounded-md px-4 py-1.5 text-sm font-bold uppercase tracking-[0.25em] -rotate-3 ${stamp.classes}`}>
                {stamp.label}
              </span>
            </div>

            {/* Parties & dates */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 py-6">
              <div>
                <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-widest mb-1">Billed To</p>
                <p className="text-sm font-semibold text-ink-900">{invoice.client?.companyName ?? `Client #${invoice.clientId}`}</p>
                {invoice.client?.address && <p className="text-xs text-ink-500 mt-1">{invoice.client.address}</p>}
                {invoice.client?.gstNumber && <p className="text-xs text-ink-500">GST No: {invoice.client.gstNumber}</p>}
              </div>
              <div>
                <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-widest mb-1">Warehouse</p>
                <p className="text-sm font-semibold text-ink-900">{invoice.warehouse?.name ?? '—'}</p>
                {invoice.warehouse?.companyGstin && <p className="text-xs text-ink-500 mt-1">GSTIN: {invoice.warehouse.companyGstin}</p>}
                {invoice.warehouse?.companyPan && <p className="text-xs text-ink-500">PAN: {invoice.warehouse.companyPan}</p>}
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-widest mb-1">Invoice Date</p>
                  <p className="text-sm font-semibold text-ink-900">{fmtDate(invoice.createdAt)}</p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-widest mb-1">Billing Period</p>
                  <p className="text-sm font-semibold text-ink-900">
                    {fmtDate(invoice.billingPeriodStart)} – {fmtDate(invoice.billingPeriodEnd)}
                  </p>
                </div>
                {invoice.status === 'paid' && invoice.paidAt && (
                  <div>
                    <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-widest mb-1">Paid On</p>
                    <p className="text-sm font-semibold text-green-700">{fmtDate(invoice.paidAt)}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] text-sm">
              <thead>
                <tr className="border-b-2 border-ink-900">
                  <th className="text-left px-6 py-3 text-xs font-bold text-ink-900 uppercase tracking-wide">Charge</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-ink-900 uppercase tracking-wide">Description</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-ink-900 uppercase tracking-wide">Qty</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-ink-900 uppercase tracking-wide">Unit</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-ink-900 uppercase tracking-wide">Rate (₹)</th>
                  <th className="text-right px-6 py-3 text-xs font-bold text-ink-900 uppercase tracking-wide">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lineItems.map((item: BillingLineItem) => {
                  const hasBreakdown = !!item.breakdown && item.breakdown.length > 0;
                  const isExpanded = expandedItems.has(item.id);
                  return (
                    <Fragment key={item.id}>
                      <tr className="border-b border-ink-200 hover:bg-ink-50">
                        <td className="px-6 py-3">
                          <span className="font-medium text-ink-800">
                            {CHARGE_LABELS[item.chargeType] ?? item.chargeType}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-ink-600 max-w-xs">
                          {item.description}
                          {hasBreakdown && (
                            <button
                              type="button"
                              className="ml-2 text-xs text-brand-600 hover:underline"
                              onClick={() => toggleBreakdown(item.id)}
                            >
                              {isExpanded ? '▾' : '▸'} {item.breakdown!.length} source{item.breakdown!.length === 1 ? '' : 's'}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-ink-700">{fmtNum(item.quantity)}</td>
                        <td className="px-4 py-3 text-ink-500">{item.unit}</td>
                        <td className="px-4 py-3 text-right font-mono text-ink-700">{fmtNum(item.rate)}</td>
                        <td className="px-6 py-3 text-right font-mono font-semibold text-ink-900">{fmtCurrency(item.amount)}</td>
                      </tr>
                      {hasBreakdown && isExpanded && (
                        <tr className="border-b border-ink-200 bg-ink-50">
                          <td colSpan={6} className="px-6 py-3">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-ink-500">
                                  <th className="text-left py-1 pr-4 font-semibold uppercase tracking-wide">Type</th>
                                  <th className="text-left py-1 pr-4 font-semibold uppercase tracking-wide">Number</th>
                                  <th className="text-left py-1 pr-4 font-semibold uppercase tracking-wide">Date</th>
                                  <th className="text-right py-1 font-semibold uppercase tracking-wide">Detail</th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.breakdown!.map((src, idx) => (
                                  <tr key={idx}>
                                    <td className="py-1 pr-4 text-ink-600">{REFERENCE_TYPE_LABELS[src.referenceType] ?? src.referenceType}</td>
                                    <td className="py-1 pr-4 font-mono text-ink-800">{src.referenceNumber}</td>
                                    <td className="py-1 pr-4 text-ink-600">{fmtDate(src.date)}</td>
                                    <td className="py-1 text-right font-mono text-ink-700">
                                      {src.weightTons != null
                                        ? `${src.weightTons.toFixed(3)} MT`
                                        : src.pallets != null
                                        ? `${src.pallets} pallets × ${src.days ?? 0}d`
                                        : `${src.sqft ?? 0} sqft × ${src.days ?? 0}d`}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {invoice.lineItems.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-ink-400">No line items</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-ink-900">
                  <td colSpan={5} className="px-4 pt-4 pb-1.5 text-right text-ink-600">Subtotal</td>
                  <td className="px-6 pt-4 pb-1.5 text-right font-mono text-ink-700">{fmtCurrency(subtotal)}</td>
                </tr>
                {cgstPct > 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-1.5 text-right text-ink-600">CGST</td>
                    <td className="px-6 py-1.5 text-right font-mono text-ink-700">{fmtCurrency(cgstAmount)}</td>
                  </tr>
                )}
                {sgstPct > 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-1.5 text-right text-ink-600">SGST</td>
                    <td className="px-6 py-1.5 text-right font-mono text-ink-700">{fmtCurrency(sgstAmount)}</td>
                  </tr>
                )}
                {roundOff !== 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-1.5 text-right text-ink-600">ROUND OFF</td>
                    <td className="px-6 py-1.5 text-right font-mono text-ink-700">
                      {roundOff < 0 ? `(-)${Math.abs(roundOff).toFixed(2)}` : roundOff.toFixed(2)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan={5} className="px-4 py-3 text-right text-base font-bold uppercase tracking-wide text-ink-900">Gross Total</td>
                  <td className="px-6 py-3 text-right font-mono text-lg font-bold text-ink-900 whitespace-nowrap">{fmtCurrency(total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

            <p className="mt-4 text-sm font-semibold text-ink-800">{amountInWordsInr(total)}</p>

            {/* Notes */}
            {invoice.notes && (
              <div className="mt-8 pt-4 border-t border-ink-200">
                <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-widest mb-1">Remark</p>
                <p className="text-sm text-ink-700">{invoice.notes}</p>
              </div>
            )}

            {/* Bank details */}
            {(invoice.warehouse?.bankName || invoice.warehouse?.bankAccountNo) && (
              <div className="mt-8 pt-4 border-t border-ink-200">
                <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-widest mb-1">Company's Bank Details</p>
                {invoice.warehouse?.bankName && <p className="text-sm text-ink-700">Bank Name: {invoice.warehouse.bankName}</p>}
                {invoice.warehouse?.bankAccountNo && <p className="text-sm text-ink-700">A/c No.: {invoice.warehouse.bankAccountNo}</p>}
                {(invoice.warehouse?.bankBranch || invoice.warehouse?.bankIfsc) && (
                  <p className="text-sm text-ink-700">
                    Branch &amp; IFS Code: {[invoice.warehouse?.bankBranch, invoice.warehouse?.bankIfsc].filter(Boolean).join(' & ')}
                  </p>
                )}
              </div>
            )}

            {/* GST e-invoice (IRN/ACK) */}
            <div className="mt-8 pt-4 border-t border-ink-200">
              <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-widest mb-2">GST E-Invoice (IRN / ACK)</p>
              <p className="text-xs text-ink-500 mb-3">
                Generate the e-invoice separately on the govt e-invoice portal, then paste the IRN/ACK details here.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <input
                    type="text"
                    placeholder="IRN No."
                    maxLength={100}
                    className={`w-full border rounded-md px-3 py-1.5 text-sm ${einvoiceErrors.irnNo ? 'border-red-400 focus:ring-red-400' : 'border-ink-300'}`}
                    value={einvoiceForm.irnNo}
                    onChange={(e) => {
                      setEinvoiceForm((prev) => ({ ...prev, irnNo: e.target.value }));
                      setEinvoiceErrors((prev) => (prev.irnNo ? { ...prev, irnNo: undefined } : prev));
                    }}
                  />
                  {einvoiceErrors.irnNo && (
                    <p className="mt-1 text-xs text-red-600">{einvoiceErrors.irnNo}</p>
                  )}
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="ACK No."
                    maxLength={50}
                    className={`w-full border rounded-md px-3 py-1.5 text-sm ${einvoiceErrors.ackNo ? 'border-red-400 focus:ring-red-400' : 'border-ink-300'}`}
                    value={einvoiceForm.ackNo}
                    onChange={(e) => {
                      setEinvoiceForm((prev) => ({ ...prev, ackNo: e.target.value }));
                      setEinvoiceErrors((prev) => (prev.ackNo ? { ...prev, ackNo: undefined } : prev));
                    }}
                  />
                  {einvoiceErrors.ackNo && (
                    <p className="mt-1 text-xs text-red-600">{einvoiceErrors.ackNo}</p>
                  )}
                </div>
                <input
                  type="date"
                  placeholder="ACK Date"
                  className="border border-ink-300 rounded-md px-3 py-1.5 text-sm"
                  value={einvoiceForm.ackDate}
                  onChange={(e) => setEinvoiceForm((prev) => ({ ...prev, ackDate: e.target.value }))}
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                disabled={einvoiceMutation.isPending}
                onClick={saveEinvoice}
              >
                {einvoiceMutation.isPending ? 'Saving…' : 'Save E-Invoice Details'}
              </Button>
            </div>

            <p className="mt-10 text-center text-xs text-ink-400 tracking-wide">
              This is a computer-generated invoice.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
